// Archive a space or a track: one confirmation that says what happens, then out of every list until restored, with
// Undo in the toast. Settings lists what is archived (Restore) and the activities deleted in the last 30 days (Restore).
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useChange } from '../lib/queries';
import { ago } from '../lib/format';
import { useToast } from './toast';
import { ErrorNote } from './bits';

/** Archive, then "Archive <name>?" with what it means; after it, go to `then` with Undo in the toast. */
export function ArchiveButton({ kind, id, name, count, then }: { kind: 'space' | 'track'; id: string; name: string; count: number; then: string }) {
  const [asking, setAsking] = useState(false);
  const toast = useToast();
  const nav = useNavigate();
  const qc = useQueryClient();
  // Archiving takes this button off the screen (its card or page goes), so what follows runs from the promise, not
  // from mutate's callbacks (which do not run once the component is gone), and Undo does not depend on it either.
  const restore = () => (kind === 'space' ? api.spaces.restore(id) : api.tracks.restore(id)).then(() => qc.invalidateQueries());
  const run = useChange(() => (kind === 'space' ? api.spaces.archive(id) : api.tracks.archive(id).then(() => undefined)));
  if (!asking) return <button type="button" className="bt sm" onClick={() => setAsking(true)}>Archive {kind}</button>;
  return (
    <div className="archq" role="alertdialog" aria-label={`Archive ${name}?`}>
      <span className="xs">Archive {name}? It and its {count} {kind === 'space' ? (count === 1 ? 'track' : 'tracks') : (count === 1 ? 'activity' : 'activities')} leave every list, Today, the calendar and search until restored. Nothing is deleted.</span>
      <button type="button" className="bt sm pri" disabled={run.isPending} onClick={() => void run.mutateAsync(undefined).then(() => {
        nav(then);
        toast(`Archived ${name}. Restore it from Settings, Archived.`, { label: 'Undo', run: () => void restore() });
      }, () => undefined)}>Archive</button>
      <button type="button" className="bt sm" onClick={() => setAsking(false)}>Cancel</button>
      {run.error ? <ErrorNote error={run.error} /> : null}
    </div>
  );
}

/** Settings, Archived: spaces (admins and the director) and tracks, each with Restore. */
export function ArchivedPanel() {
  const q = useQuery({ queryKey: ['archived'], queryFn: api.archived });
  const toast = useToast();
  const space = useChange((id: string) => api.spaces.restore(id));
  const track = useChange((id: string) => api.tracks.restore(id));
  const d = q.data;
  if (!d || (!d.spaces.length && !d.tracks.length)) return (
    <div className="panel" role="region" aria-label="Archived"><div className="sh"><b>Archived</b></div><div className="xs mut">Nothing is archived. Archive a space here (Spaces) or a track from its page.</div></div>
  );
  return (
    <div className="panel" role="region" aria-label="Archived">
      <div className="sh" style={{ marginBottom: 6 }}><b>Archived</b><span>{d.spaces.length + d.tracks.length}</span></div>
      {d.spaces.map(s => (
        <div className="lrow" key={s.id} data-archived={s.name}>
          <div style={{ flex: 1, minWidth: 0 }}><div className="sm">{s.name}</div><div className="xs mut">Space · {s.tracks} {s.tracks === 1 ? 'track' : 'tracks'} · archived {ago(s.archivedAt)}{s.archivedBy ? ` by ${s.archivedBy}` : ''}</div></div>
          <button type="button" className="bt sm" disabled={space.isPending} onClick={() => space.mutate(s.id, { onSuccess: () => toast(`${s.name} is back.`) })}>Restore</button>
        </div>
      ))}
      {d.tracks.map(t => (
        <div className="lrow" key={t.id} data-archived={t.name}>
          <div style={{ flex: 1, minWidth: 0 }}><div className="sm">{t.name}</div><div className="xs mut">Track in <Link className="link" to={`/spaces/${t.spaceId}`}>{t.spaceName}</Link> · {t.activities} {t.activities === 1 ? 'activity' : 'activities'} · archived {ago(t.archivedAt)}{t.archivedBy ? ` by ${t.archivedBy}` : ''}</div></div>
          <button type="button" className="bt sm" disabled={track.isPending} onClick={() => track.mutate(t.id, { onSuccess: () => toast(`${t.name} is back.`) })}>Restore</button>
        </div>
      ))}
      {space.error || track.error ? <ErrorNote error={space.error ?? track.error} /> : null}
    </div>
  );
}

/** Settings, Deleted: activities deleted in the last 30 days that you can see, with Restore. */
export function DeletedPanel() {
  const q = useQuery({ queryKey: ['deleted'], queryFn: api.deleted });
  const toast = useToast();
  const restore = useChange((id: string) => api.activities.restore(id));
  const d = q.data;
  return (
    <div className="panel" role="region" aria-label="Deleted">
      <div className="sh" style={{ marginBottom: 6 }}><b>Deleted</b><span>Last {d?.days ?? 30} days</span></div>
      {d && !d.activities.length && <div className="xs mut">Nothing deleted in the last {d.days} days.</div>}
      {d?.activities.map(a => (
        <div className="lrow" key={a.id} data-deleted={a.title}>
          <div style={{ flex: 1, minWidth: 0 }}><div className="sm">{a.title}</div><div className="xs mut">{a.type[0]!.toUpperCase() + a.type.slice(1)} · {a.where} · deleted {ago(a.deletedAt)}{a.deletedBy ? ` by ${a.deletedBy}` : ''}</div></div>
          <button type="button" className="bt sm" disabled={restore.isPending} onClick={() => restore.mutate(a.id, { onSuccess: r => toast(`${r.title} is back.`) })}>Restore</button>
        </div>
      ))}
      {restore.error ? <ErrorNote error={restore.error} /> : null}
    </div>
  );
}
